// Shared accessible wrapper for a single field. Renders the label (with a
// required indicator), optional description, help text (positioned above or
// below the control per `help_position`), the control itself, and any
// validation errors as Bootstrap `invalid-feedback`. The control — passed as
// children — owns its own id / aria attributes (derived from the same path via
// ids.ts), so this wrapper only needs to render the matching label and
// described-by targets.
//
// The label is always a real `<label>` element: the app's label typography comes
// from a bare `label { ... }` element selector (forms-universal/_inputs.scss), so
// a `<span class="form-label">` renders in plain body text with its vertical
// margin dropped (margins are ignored on inline elements). Grouped controls just
// omit `htmlFor` — see `groupLabel`.

import type { ReactNode } from 'react'
import type { DynamicFormField, ValidationError } from './types'
import { Caption } from './caption'
import { descriptionIdFor, errorIdFor, fieldIdFor, helpIdFor, labelIdFor } from './ids'
import { useFieldContext } from './rendererContext'

interface FieldShellProps {
  field: DynamicFormField
  path: string
  errors: ValidationError[]
  /** The field's control is a group (radiogroup / role=group / button toggle)
   * rather than one labelable element, so the label carries no `htmlFor` — the
   * group is named via `aria-labelledby` pointing at this label's id instead. */
  groupLabel?: boolean
  /** Suppress the top label entirely — e.g. boolean checkboxes that render
   * their own form-check-label beside the control. */
  hideLabel?: boolean
  children: ReactNode
}

export function FieldShell({
  field,
  path,
  errors,
  groupLabel = false,
  hideLabel = false,
  children,
}: FieldShellProps) {
  const { classNames, hideFieldLabels, labelSuffix } = useFieldContext()
  const controlId = fieldIdFor(path)
  const helpAbove = field.help_position === 'above'

  // In a table-layout group the column header is the visible label, so keep the
  // <label> (accessible name intact) but render it visually hidden.
  const labelClassName = hideFieldLabels
    ? 'visually-hidden'
    : (classNames.label ?? 'form-label')

  const labelSuffixNode = labelSuffix?.(path) ?? null
  const labelInner = (
    <>
      <Caption field={field} text={field.label ?? ''} />
      {field.required ? <RequiredIndicator className={classNames.requiredIndicator} /> : null}
      {labelSuffixNode}
    </>
  )

  const label =
    hideLabel || field.label == null || field.label === '' ? null : (
      <label
        id={labelIdFor(path)}
        className={labelClassName}
        htmlFor={groupLabel ? undefined : controlId}
      >
        {labelInner}
      </label>
    )

  const description = field.description ? (
    <div id={descriptionIdFor(path)} className={classNames.description ?? 'form-text'}>
      <Caption field={field} text={field.description} />
    </div>
  ) : null

  const help = field.help_text ? (
    <small id={helpIdFor(path)} className={classNames.helpText ?? 'form-text text-muted d-block'}>
      <Caption field={field} text={field.help_text} />
    </small>
  ) : null

  return (
    <div className={classNames.fieldContainer ?? 'mb-3'}>
      {label}
      {description}
      {helpAbove ? help : null}
      {children}
      {helpAbove ? null : help}
      {errors.length > 0 ? (
        <div id={errorIdFor(path)} className={classNames.invalidFeedback ?? 'invalid-feedback d-block'}>
          {errors.map((error, i) => (
            <div key={`${error.code}-${i}`}>{error.message}</div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function RequiredIndicator({ className }: { className?: string }) {
  // The visible `*` is hidden from assistive tech (it would read as "asterisk");
  // the visually-hidden "(required)" carries the requirement into the control's
  // accessible name instead — works for label-pointed controls and for grouped
  // controls whose name comes via aria-labelledby to the group's label.
  return (
    <>
      <abbr className={className ?? 'text-danger ms-1'} title="required" aria-hidden="true">
        *
      </abbr>
      <span className="visually-hidden"> (required)</span>
    </>
  )
}
